// packages/api/src/hooks/useMyPerson.ts
// Source: docs/contracts/ESCR-profile-person-edit.md

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type {
  ApiSuccess,
  MyPerson,
  SubmitRegistrationCorrectionResponse,
  UpdateMyPersonInput,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

/**
 * GET /profile/person — dados de pessoa física do consultor PF/CLT.
 *
 * 404 é estado esperado (party organization / PJ sem row em persons), não
 * erro transitório: sem retry e a UI esconde a seção.
 */
export function useMyPerson() {
  return useQuery({
    queryKey: queryKeys.profile.person(),
    queryFn: async (): Promise<MyPerson> => {
      const response = await apiClient.get<ApiSuccess<MyPerson>>('/profile/person');
      return response.data.data;
    },
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * PATCH /profile/person — partial update; CPF é imutável
 * (enviar cpf → 422 no backend).
 */
export function useUpdateMyPerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateMyPersonInput): Promise<MyPerson> => {
      const response = await apiClient.patch<ApiSuccess<MyPerson>>('/profile/person', input);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.person() });
      // Corrigir identidade pode limpar a rejeição do bloco no fluxo de correção.
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}

/**
 * POST /profile/my-registration/submit-correction — ação explícita de
 * "terminei de corrigir, mande pra análise" (correções só-de-dado, sem
 * reenvio de documento — esse já auto-transiciona sozinho). Idempotente.
 */
export function useSubmitRegistrationCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SubmitRegistrationCorrectionResponse> => {
      const response = await apiClient.post<ApiSuccess<SubmitRegistrationCorrectionResponse>>(
        '/profile/my-registration/submit-correction',
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}
